/**
 * Generation Schemas - Zero-Overhead Content Generation
 *
 * This module automatically extracts generation requirements from component
 * field definitions. Developers only need to set `metadata.source: "ai"` on
 * fields that need AI generation - everything else is automatic.
 *
 * FLOW:
 * 1. Developer adds field with `metadata: { source: "ai", description: "..." }`
 * 2. This module auto-extracts all AI fields for the generator
 * 3. Generator uses these schemas to build prompts and validate output
 */

import { AllComponentFields } from "./components.registry";
import type { EditorFieldGuardrails } from "../lib/editor-field-guidance";

/**
 * Field source types
 */
export type FieldSource = "ai" | "context" | "manual" | "derived";

export type FieldHydrationTransform = "sanitizeTrustBadge";
export type FieldHydrationFallback = "none" | "businessTrustBadge";
export type FieldOverridePolicy = "userWins" | "systemWins";

export interface FieldHydrationPolicy {
  transform?: FieldHydrationTransform;
  fallback?: FieldHydrationFallback;
  overridePolicy?: FieldOverridePolicy;
}

/**
 * Schema for array item fields
 */
export interface ArrayItemFieldMeta {
  fieldName: string;
  description: string;
  type?: string;
}

/**
 * Field metadata extracted from component definitions
 */
export interface FieldGenerationMeta {
  fieldName: string;
  source: FieldSource;
  description: string;
  seo: boolean;
  key?: string;
  contextKey?: string;
  fieldType?: string;
  /**
   * Legal values for a select/radio field, copied straight off the registry
   * field definition. Present ONLY when the values are a closed set, which is
   * what lets a generated schema constrain a model to real options instead of
   * trusting it to invent a valid one.
   */
  options?: Array<{ label: string; value: unknown }>;
  promptGuidance?: string[];
  hydration?: FieldHydrationPolicy;
  guardrails?: EditorFieldGuardrails;
  /** True if this field is an array container */
  isArray?: boolean;
  /** Schema for items within an array field */
  arrayItemFields?: ArrayItemFieldMeta[];
}

/**
 * Schema for a single component's generation requirements
 */
export interface ComponentGenerationSchema {
  componentType: string;
  fields: FieldGenerationMeta[];
  aiFields: FieldGenerationMeta[];
  contextFields: FieldGenerationMeta[];
}

/**
 * Only select/radio declare a closed value set; every other field type returns
 * undefined so callers can treat "has options" as "is a choice field".
 */
function extractFieldOptions(
  fieldDef: any,
): Array<{ label: string; value: unknown }> | undefined {
  if (fieldDef?.type !== "select" && fieldDef?.type !== "radio") {
    return undefined;
  }
  if (!Array.isArray(fieldDef.options) || fieldDef.options.length === 0) {
    return undefined;
  }
  const options = fieldDef.options
    .filter((option: any) => option && typeof option === "object" && "value" in option)
    .map((option: any) => ({
      label: String(option.label ?? option.value),
      value: option.value,
    }));
  return options.length > 0 ? options : undefined;
}

/**
 * Extract all fields requiring generation from a component's field definitions
 */
function extractFieldsFromDefinition(fields: Record<string, any>): {
  fields: FieldGenerationMeta[];
  aiFields: FieldGenerationMeta[];
  contextFields: FieldGenerationMeta[];
} {
  const allFields: FieldGenerationMeta[] = [];
  const aiFields: FieldGenerationMeta[] = [];
  const contextFields: FieldGenerationMeta[] = [];

  function processField(fieldName: string, fieldDef: any) {
    const metadata = fieldDef?.metadata;
    if (!metadata?.source) return;

    const isArray = fieldDef.type === "array";
    const arrayItemFields: ArrayItemFieldMeta[] = [];

    // Extract array item field schemas
    if (isArray && fieldDef.arrayFields) {
      for (const [nestedName, nestedDef] of Object.entries(fieldDef.arrayFields)) {
        const nestedMeta = (nestedDef as any)?.metadata;
        arrayItemFields.push({
          fieldName: nestedName,
          description: nestedMeta?.description || `${nestedName} in ${fieldName}`,
          type: (nestedDef as any)?.type,
        });
      }
    }

    const meta: FieldGenerationMeta = {
      fieldName,
      source: metadata.source,
      description: metadata.description || `${fieldName} content`,
      seo: metadata.seo || false,
      key: metadata.key,
      contextKey: metadata.contextKey,
      fieldType: fieldDef.type,
      options: extractFieldOptions(fieldDef),
      promptGuidance: Array.isArray(metadata.promptGuidance)
        ? metadata.promptGuidance.filter(
            (line: unknown): line is string => typeof line === "string" && line.trim().length > 0,
          )
        : undefined,
      hydration: metadata.hydration,
      guardrails: metadata.guardrails,
      isArray,
      arrayItemFields: isArray ? arrayItemFields : undefined,
    };

    allFields.push(meta);

    if (metadata.source === "ai") {
      aiFields.push(meta);
    } else if (metadata.source === "context") {
      contextFields.push(meta);
    }

    // Also add nested array fields as separate entries (for backward compatibility)
    if (fieldDef.arrayFields) {
      for (const [nestedName, nestedDef] of Object.entries(fieldDef.arrayFields)) {
        const nestedMeta = (nestedDef as any)?.metadata;
        if (nestedMeta?.source === "ai") {
          aiFields.push({
            fieldName: `${fieldName}[].${nestedName}`,
            source: "ai",
            description: nestedMeta.description || `${nestedName} in ${fieldName}`,
            seo: nestedMeta.seo || false,
            key: nestedMeta.key,
          });
        }
      }
    }
  }

  for (const [fieldName, fieldDef] of Object.entries(fields)) {
    processField(fieldName, fieldDef);
  }

  return { fields: allFields, aiFields, contextFields };
}

/**
 * Auto-generated component schemas from AllComponentFields
 * This is rebuilt automatically when component definitions change.
 */
function buildAllSchemas(): Record<string, ComponentGenerationSchema> {
  const schemas: Record<string, ComponentGenerationSchema> = {};

  for (const [componentType, fields] of Object.entries(AllComponentFields)) {
    const {
      fields: allFields,
      aiFields,
      contextFields,
    } = extractFieldsFromDefinition(fields as Record<string, any>);
    schemas[componentType] = {
      componentType,
      fields: allFields,
      aiFields,
      contextFields,
    };
  }

  return schemas;
}

/**
 * All component generation schemas - auto-generated from field definitions
 */
export const COMPONENT_SCHEMAS = buildAllSchemas();

/**
 * Get AI-required field names for a component
 */
export function getAIFields(componentType: string): string[] {
  return COMPONENT_SCHEMAS[componentType]?.aiFields.map((f) => f.fieldName) || [];
}

/**
 * Get detailed AI field metadata for a component
 */
export function getAIFieldsMeta(componentType: string): FieldGenerationMeta[] {
  return COMPONENT_SCHEMAS[componentType]?.aiFields || [];
}

/**
 * Fields whose legal values are a closed set — layout, variant, alignment. These
 * are the knobs a model can be handed safely: the option list becomes an enum in
 * the generated schema, so an invalid value is unrepresentable rather than merely
 * rejected after the fact.
 */
export function getChoiceFieldsMeta(componentType: string): FieldGenerationMeta[] {
  return (COMPONENT_SCHEMAS[componentType]?.fields ?? []).filter(
    (field) => field.options !== undefined && field.options.length > 0,
  );
}

/**
 * Get all field metadata for a component, not just AI fields.
 */
export function getComponentFieldsMeta(componentType: string): FieldGenerationMeta[] {
  return COMPONENT_SCHEMAS[componentType]?.fields || [];
}

/**
 * Build AI prompt section for a component's fields
 * Returns field descriptions that can be added to an AI prompt
 */
export function buildFieldPromptSection(componentType: string): string {
  const fields = getAIFieldsMeta(componentType);
  if (fields.length === 0) return "";

  return fields.map((f) => `- ${f.fieldName}: ${f.description}`).join("\n");
}

/**
 * Validate that props include all AI-required fields
 */
export function validateProps(
  componentType: string,
  props: Record<string, any>,
): { valid: boolean; missing: string[] } {
  const required = getAIFields(componentType);
  const missing = required.filter((field) => {
    // Handle nested fields like "services[].title"
    if (field.includes("[].")) {
      const [arrayField] = field.split("[].");
      const arr = props[arrayField];
      // Array fields are valid if array exists (items validated separately)
      return !Array.isArray(arr);
    }
    const value = props[field];
    return value === undefined || value === null || value === "";
  });

  return { valid: missing.length === 0, missing };
}

/**
 * Get all components that have AI-generated fields
 */
export function getComponentsWithAIFields(): string[] {
  return Object.entries(COMPONENT_SCHEMAS)
    .filter(([_, schema]) => schema.aiFields.length > 0)
    .map(([type]) => type);
}

/**
 * Debug: Print all AI fields across all components
 */
export function debugPrintAllAIFields(): void {
  console.log("=== AI-Generated Fields by Component ===\n");
  for (const [type, schema] of Object.entries(COMPONENT_SCHEMAS)) {
    if (schema.aiFields.length === 0) continue;
    console.log(`${type}:`);
    for (const field of schema.aiFields) {
      console.log(`  - ${field.fieldName}: ${field.description}`);
    }
    console.log();
  }
}

export type ComponentType = keyof typeof COMPONENT_SCHEMAS;
